import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, ArrowRight, Users, Building2, HandHeart, ShieldCheck, Lightbulb, Check } from "lucide-react";
import Seo, { buildBreadcrumbLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import { company, whatsappUrl } from "../data/site";
import vanImg from "../assets/rt-van.webp";
import vanBrandedImg from "../assets/rt-van-branded.webp";
import sponsorImg from "../assets/sponsor-tsv-neu-ulm-spartans.webp";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Über uns", to: "/ueber-uns" },
];

const workPoints = [
  "persönliche Abstimmung",
  "klare Angebotserstellung",
  "sorgfältige Durchführung",
  "flexible Lösungen nach Bedarf",
];

const cards = [
  {
    icon: HandHeart,
    title: "Respektvoll",
    text: "Wir gehen sorgfältig mit dem Objekt und diskret mit persönlichen oder sensiblen Situationen um.",
  },
  {
    icon: ShieldCheck,
    title: "Zuverlässig",
    text: "Vereinbarte Leistungen, Termine und Abläufe werden klar abgestimmt und nachvollziehbar umgesetzt.",
  },
  {
    icon: Lightbulb,
    title: "Lösungsorientiert",
    text: "Von einzelnen Räumen bis zu umfangreichen Räumungen entwickeln wir eine passende Lösung für den jeweiligen Auftrag.",
  },
];

export default function UeberUns() {
  return (
    <div data-testid="page-ueber-uns" className="bg-white">
      <Seo
        path="/ueber-uns"
        title="Über uns | Thomas Dietrich – Rümpeltaxi aus Altenstadt (Iller)"
        description="Rümpeltaxi ist der Betrieb von Thomas Dietrich in Altenstadt bei Illertissen. Persönliche Unterstützung bei Entrümpelung, Haushaltsauflösung, Entsorgung und Umzug in Ulm, Neu-Ulm und Region."
        jsonLd={buildBreadcrumbLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />

      {/* HERO */}
      <section className="bg-white py-16 md:py-24" data-testid="ueberuns-hero">
        <div className="container-page grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-16">
          <Reveal>
            <span className="kicker">Über Rümpeltaxi</span>
            <h1 className="mt-3 max-w-xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-anthracite sm:text-5xl lg:text-6xl">
              Persönlich, zuverlässig und anpackend.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mid-gray md:text-lg">
              Hinter Rümpeltaxi steht Inhaber Thomas Dietrich. Wir unterstützen Privatkunden,
              Eigentümer, Angehörige, Betreuer und Unternehmen bei Entrümpelungen,
              Haushaltsauflösungen, Entsorgungen, Transporten und Umzügen.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-mid-gray md:text-lg" data-testid="ueberuns-galabau-hinweis">
              Neben Entrümpelung und Räumung bieten wir auch Garten- und Landschaftsbau (Galabau) sowie
              Transporte an – aus einer Hand, persönlich aus Altenstadt.
            </p>
          </Reveal>
          <Reveal delay={1}>
            <figure className="overflow-hidden rounded-md border-4 border-anthracite">
              <img
                src={vanImg}
                alt="Rümpeltaxi-Transporter im Einsatz"
                className="h-full max-h-[520px] w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </figure>
          </Reveal>
        </div>
      </section>

      {/* UNSER TRANSPORTER */}
      <section className="bg-white pb-16 md:pb-24" data-testid="ueberuns-van">
        <div className="container-page">
          <Reveal>
            <figure className="overflow-hidden rounded-md border-4 border-anthracite">
              <img
                src={vanBrandedImg}
                alt="Rümpeltaxi Transporter gelb-schwarz, Entrümpelung Ulm Neu-Ulm Altenstadt"
                className="w-full object-cover"
                loading="lazy"
                decoding="async"
                data-testid="ueberuns-van-image"
              />
              <figcaption className="bg-anthracite px-5 py-3 text-sm font-semibold text-white" data-testid="ueberuns-van-caption">
                Unser Rümpeltaxi aus Altenstadt bei Ulm.
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={1}>
            <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center" data-testid="ueberuns-sponsor">
              <img
                src={sponsorImg}
                alt="Offizieller Sponsor TSV Neu-Ulm Spartans – Rümpeltaxi Altenstadt"
                className="w-full max-w-[400px] rounded-sm"
                loading="lazy"
                decoding="async"
                data-testid="ueberuns-sponsor-image"
              />
              <p className="text-base leading-relaxed text-mid-gray md:text-lg">
                Rümpeltaxi ist offizieller Sponsor der TSV Neu-Ulm Spartans.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PERSÖNLICH */}
      <section className="bg-light-gray py-16 md:py-24" data-testid="ueberuns-personal">
        <div className="container-page grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
          <Reveal>
            <span className="kicker">Direkte Kommunikation</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
              Direkte Kommunikation statt komplizierter Abläufe
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-mid-gray md:text-lg">
              Von der ersten Anfrage bis zur Übergabe haben Sie bei Rümpeltaxi einen direkten
              Ansprechpartner. Jeder Auftrag wird individuell abgestimmt und passend zum Objekt,
              zum Umfang und zur jeweiligen Situation geplant.
            </p>
          </Reveal>
          <Reveal delay={1}>
            <ul className="grid gap-3 sm:grid-cols-2">
              {workPoints.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 rounded-sm border border-white bg-white p-4"
                  data-testid={`ueberuns-point-${p}`}
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-yellow text-brand-black">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-anthracite md:text-base">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ARBEITSWEISE */}
      <section className="bg-white py-16 md:py-24" data-testid="ueberuns-cards">
        <div className="container-page">
          <Reveal className="max-w-2xl">
            <span className="kicker">Arbeitsweise</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
              Worauf Sie sich verlassen können
            </h2>
          </Reveal>
          <StaggerGroup className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((c) => (
              <motion.div
                key={c.title}
                variants={itemVariants}
                className="flex h-full flex-col rounded-sm border border-light-gray bg-white p-7 transition-shadow hover:shadow-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-light-gray text-anthracite">
                  <c.icon className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-anthracite">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mid-gray md:text-base">{c.text}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* PRIVAT / GESCHÄFT */}
      <section className="bg-light-gray py-16 md:py-24" data-testid="ueberuns-audiences">
        <div className="container-page grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col rounded-sm border border-white bg-white p-8 md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-yellow text-brand-black">
                <Users className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold text-anthracite">
                Für Privatkunden
              </h3>
              <p className="mt-4 flex-1 text-base leading-relaxed text-mid-gray">
                Unterstützung bei Haushaltsauflösungen, Nachlässen, Umzügen, Immobilienverkäufen
                sowie der Räumung einzelner Räume oder kompletter Objekte.
              </p>
              <Link
                to="/privatkunden"
                className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-anthracite hover:text-yellow-dark"
                data-testid="ueberuns-private-link"
              >
                Leistungen für Privatkunden <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <div className="flex h-full flex-col rounded-sm border border-dark-gray bg-brand-black p-8 text-white md:p-10">
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-yellow text-brand-black">
                <Building2 className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold">
                Für Unternehmen und Institutionen
              </h3>
              <p className="mt-4 flex-1 text-base leading-relaxed text-light-gray/70">
                Planbare Räumungs-, Transport- und Entsorgungsleistungen für Hausverwaltungen,
                Betreuer, Kanzleien, Unternehmen und soziale Einrichtungen.
              </p>
              <Link
                to="/geschaeftskunden"
                className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-yellow hover:text-yellow-dark"
                data-testid="ueberuns-business-link"
              >
                Leistungen für Geschäftskunden <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABSCHLUSS */}
      <section className="bg-brand-black py-16 text-white md:py-24" data-testid="ueberuns-cta">
        <div className="container-page max-w-3xl">
          <Reveal>
            <h2 className="font-heading text-3xl font-bold tracking-tight md:text-4xl">
              Sie möchten Ihr Vorhaben persönlich besprechen?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-light-gray/80 md:text-lg">
              Rufen Sie uns an oder senden Sie uns kurz die wichtigsten Informationen zu Ihrem
              Objekt. Auf Wunsch können Sie direkt Bilder mitsenden.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/kontakt"
                data-testid="ueberuns-cta-anfrage"
                className="inline-flex items-center justify-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark"
              >
                Unverbindlich anfragen <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={company.phoneLink}
                data-testid="ueberuns-cta-call"
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 bg-white/5 px-6 py-3.5 font-heading font-bold text-white transition-colors hover:bg-white/10"
              >
                <Phone className="h-4 w-4" /> {company.phoneDisplay} anrufen
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ueberuns-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/20 bg-white/5 px-6 py-3.5 font-heading font-bold text-white transition-colors hover:bg-white/10"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp öffnen
              </a>
            </div>
            <p className="mt-8 text-sm text-light-gray/60">
              <Link to="/leistungen" className="underline hover:text-yellow">
                Alle Leistungen
              </Link>
              {" · "}
              <Link to="/referenzen" className="underline hover:text-yellow">
                Referenzen
              </Link>
              {" · "}
              <Link to="/kontakt" className="underline hover:text-yellow">
                Kontakt
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
